import useModal from '@area2k/use-modal';
import { Card, CardContent, Stack } from '@mui/material';
import { useStripe } from '@stripe/react-stripe-js';
import { useState } from 'react';

import SelectAccountModal from './SelectAccountModal';

import { SubmitErrorAlert } from '@/components/Alerts';
import Button from '@/components/Button';
import Option from '@/components/Option';
import Ach, { AchConfigProps } from '@/components/PaymentMethod/Ach';
import { Heading, Subheading } from '@/components/Typography';
import Text from '@/elements/Text';
import {
  useAttachBankAccountMutation,
  useAttachCardToAccountMutation,
  useCreateAccountMutation,
  useSetCurrentPaymentMethodMutation,
} from '@/graphql';
import useAuth from '@/hooks/useAuth';
import AddCardForm from '@/routes/Agency/Account/PaymentSection/AddCardForm';
import { AccountFormValues } from '@/routes/Agency/common/components/AccountForm';
import { isWeb } from '@/util/platform';

interface Props {
  getBillingValues: () => AccountFormValues;
  custId: string;
  handleCancel: () => void;
  handleSuccess: () => void;
  phoneOnly: boolean;
}
const paymentMethodOptions = [
  { label: 'Check', value: 'check' },
  { label: 'ACH', value: 'ach' },
  { label: 'Credit Card', value: 'card' },
];

const AddPaymentMethod = ({
  getBillingValues,
  custId,
  handleCancel,
  handleSuccess,
  phoneOnly,
}: Props) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [achConfig, setAchConfig] = useState<AchConfigProps>();
  const [bankAccountInfo, setBankAccInfo] = useState<any>();

  const { currentAdminIsCustomerAdmin } = useAuth();

  const [createAccount] = useCreateAccountMutation();
  const [setCurrentPaymentMethod] = useSetCurrentPaymentMethodMutation();
  const stripe = isWeb() ? useStripe() : undefined;
  const [attachCardToAccount] = useAttachCardToAccountMutation();
  const [attachBankAccountToAccount] = useAttachBankAccountMutation();

  const [showAccountsModal, hideAccountModal] = useModal(
    () => (
      <SelectAccountModal
        achConfig={achConfig}
        handleSelect={(info) => {
          setBankAccInfo(info);
          hideAccountModal();
        }}
        hideAccountModal={hideAccountModal}
      />
    ),
    [achConfig]
  );

  const createBillingAccount = async () => {
    const values = getBillingValues();
    const address = values.address;
    const res = await createAccount({
      variables: {
        customerId: custId,
        name: values.accountName,
        defaultContactId: values.defaultContact.id,
        ...address,
        addressLine2: address.addressLine2 ?? null,
        cutOff: values.invoiceDuration,
        monthlyCutOff: values.crossMonthInvoice,
        billingWeek: values.billingWeek,
        consolidateUnprocessedCharges: values.invoiceSweepRule,
        groupBy: values.groupBy,
      },
    });
    return res;
  };
  const setPaymentMethod = async (accountId, paymentMethodId) => {
    return await setCurrentPaymentMethod({
      variables: {
        account: accountId,
        paymentMethodId,
      },
    });
  };

  const handleSave = async (info: Record<string, any>) => {
    setLoading(true);
    try {
      const result = await createBillingAccount();
      const account = result?.data?.accountCreate?.account;
      if (account) {
        if (selectedPaymentMethod === 'card' && info && stripe) {
          const resp = await stripe.createPaymentMethod({
            type: 'card',
            card: info.cardElement,
            billing_details: {
              name: info.name || '',
            },
          });
          if (resp.paymentMethod) {
            await attachCardToAccount({
              variables: {
                account: account.id,
                paymentMethodId: resp.paymentMethod.id,
              },
            });
            await setPaymentMethod(account.id, resp.paymentMethod.id);
          }
        } else if (selectedPaymentMethod === 'ach' && bankAccountInfo) {
          const resp = await attachBankAccountToAccount({
            variables: {
              account: account.id,
              bankAccountId: bankAccountInfo.bankAccountId,
              publicToken: bankAccountInfo.publicToken,
            },
          });

          const paymentId = resp.data?.accountAttachBankAccount.bankAccount?.id;
          if (paymentId) {
            await setPaymentMethod(account.id, paymentId);
          }
        } else {
          await setPaymentMethod(account.id, 'check');
        }
        handleSuccess();
      }
    } catch (e: any) {
      setError(e?.message || 'Please contact support.');
    }
    setLoading(false);
  };

  return (
    <>
      <Heading>Add payment method</Heading>
      {!currentAdminIsCustomerAdmin && (
        <Stack direction={'row'} gap={2} sx={{ my: '4px' }}>
          {paymentMethodOptions.map((option, index) => (
            <Option
              key={index}
              checked={selectedPaymentMethod === option.value}
              id={`paymentMethod${index}`}
              label={option.label}
              name="paymentMethod"
              type="radio"
              value={option.value}
              onChange={({ currentTarget: { value: paymentMethodType } }) => {
                setSelectedPaymentMethod(paymentMethodType);
              }}
            />
          ))}
        </Stack>
      )}
      {error && (
        <div style={{ width: '100%' }}>
          <SubmitErrorAlert description={error} status={'danger'} />
        </div>
      )}
      {selectedPaymentMethod === 'card' && (
        <AddCardForm
          adjustUI={true}
          handleAddCard={handleSave}
          isLoading={loading}
          phoneOnly={phoneOnly}
          onCloseEditor={handleCancel}
        />
      )}
      {selectedPaymentMethod === 'ach' && (
        <Card
          sx={{ border: `1px solid #D3D3D3`, width: '100%' }}
          variant="outlined"
        >
          <CardContent>
            <Subheading css={{ marginBottom: '10px' }}>ACH account</Subheading>
            {bankAccountInfo ? (
              <Text>Account Added.Click Save to continue</Text>
            ) : (
              <Ach
                bankAccounts={[]}
                handleSuccess={(config) => {
                  setAchConfig(config);
                  showAccountsModal();
                }}
                leftSpace=""
                selectedPaymentMethodId={null}
                setSelectedPaymentMethodId={() => {}}
              />
            )}
          </CardContent>
        </Card>
      )}

      {selectedPaymentMethod !== 'card' && (
        <Stack direction={'row'} gap={1.5} sx={{ marginTop: '8px' }}>
          <Button
            a11yLabel="Save Credit Card"
            disabled={selectedPaymentMethod === 'ach' && !bankAccountInfo}
            id="add-payment-btn"
            isLoading={loading}
            label="Save"
            type="button"
            onClick={handleSave}
          />
          <Button
            a11yLabel="Go back"
            appearance="outline"
            id="cancel-btn"
            label="Cancel"
            type="button"
            onClick={handleCancel}
          />
        </Stack>
      )}
    </>
  );
};

export default AddPaymentMethod;
